
import { useState } from 'react';

const ConsultationForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    project_type: '',
    application: '',
    timeline: '',
    budget_range: '',
    technical_requirements: '',
    current_challenges: '',
    preferred_contact: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/consultation_request', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          project_type: '',
          application: '',
          timeline: '',
          budget_range: '',
          technical_requirements: '',
          current_challenges: '',
          preferred_contact: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <h2 className="text-3xl font-bold text-gray-900 mb-6">
        Submit Your Consultation Request
      </h2>
      <p className="text-gray-600 mb-8">
        Fill out the form below and our technical team will handle your request shortly. 
        We typically respond within 24 hours with a detailed analysis.
      </p>

      {submitStatus === 'success' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <p className="text-green-800 font-semibold">
            ✓ Request submitted successfully! Our technical team will contact you shortly.
          </p>
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800 font-semibold">
            ✗ Error submitting request. Please try again or contact us directly.
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} data-readdy-form id="consultation_request">
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="Enter your full name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="your.email@company.com"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company Name *
            </label>
            <input
              type="text"
              name="company"
              value={formData.company}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="Your company name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="+971 XX XXX XXXX"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Project Type *
            </label>
            <select
              name="project_type"
              value={formData.project_type}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8"
            >
              <option value="">Select project type</option>
              <option value="upstream">Upstream Operations</option>
              <option value="midstream">Midstream Operations</option>
              <option value="downstream">Downstream Operations</option>
              <option value="production">Production Enhancement</option>
              <option value="maintenance">Maintenance & Protection</option>
              <option value="custom">Custom Solution</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Application Area
            </label>
            <select
              name="application"
              value={formData.application}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8"
            >
              <option value="">Select application</option>
              <option value="corrosion_mitigation">Corrosion Mitigation</option>
              <option value="erosion_control">Erosion Control</option>
              <option value="scale_inhibition">Scale Inhibition</option>
              <option value="scale_analysis">Scale Analysis</option>
              <option value="stimulation">Well Stimulation</option>
              <option value="pumping_services">Pumping Services</option>
              <option value="drilling_fluids">Drilling Fluids</option>
              <option value="water_treatment">Water Treatment</option>
            </select>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Project Timeline
            </label>
            <select
              name="timeline"
              value={formData.timeline}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8"
            >
              <option value="">Select timeline</option>
              <option value="immediate">Immediate (Within 1 month)</option>
              <option value="short_term">Short-term (1-3 months)</option>
              <option value="medium_term">Medium-term (3-6 months)</option>
              <option value="long_term">Long-term (6+ months)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preferred Contact Method
            </label>
            <select
              name="preferred_contact"
              value={formData.preferred_contact}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8"
            >
              <option value="">Select contact method</option>
              <option value="email">Email</option>
              <option value="phone">Phone Call</option>
              <option value="meeting">In-person Meeting</option>
              <option value="video_call">Video Conference</option>
            </select>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Technical Requirements *
          </label>
          <textarea
            name="technical_requirements"
            value={formData.technical_requirements}
            onChange={handleChange}
            required
            rows={4}
            maxLength={500}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            placeholder="Describe your technical requirements, specifications, and performance criteria..."
          />
          <p className="text-xs text-gray-500 mt-1">
            {formData.technical_requirements.length}/500 characters
          </p>
        </div>

        <div className="mb-8">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Current Challenges
          </label>
          <textarea
            name="current_challenges"
            value={formData.current_challenges}
            onChange={handleChange}
            rows={3}
            maxLength={500}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            placeholder="Describe any current operational challenges or issues you're facing..."
          />
          <p className="text-xs text-gray-500 mt-1">
            {formData.current_challenges.length}/500 characters
          </p>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-teal-600 text-white py-4 px-6 rounded-lg text-lg font-semibold hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
        >
          {isSubmitting ? 'Submitting Request...' : 'Submit Consultation Request'}
        </button>
      </form>
    </div>
  );
};

export default ConsultationForm;
